import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";

const Page = styled.main`
  padding: 40px 18px;
  min-height: 70vh;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
  letter-spacing: 0.3px;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  max-width: 820px;
  color: #4b5563;
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const Btn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 10px;
  text-decoration: none;

  background: #0f766e;
  color: #fff;
  font-weight: 900;

  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.10);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.03);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const BtnGhost = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 10px;
  text-decoration: none;

  background: #fff;
  color: #111827;
  font-weight: 900;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.35);
    outline-offset: 3px;
  }
`;

const Grid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
`;

const Card = styled.li`
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 44px rgba(0, 0, 0, 0.10);
  }
`;

const CardLink = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
`;

const CardInner = styled.div`
  padding: 14px;
`;

const Logo = styled.img`
  width: 100%;
  height: 120px;
  object-fit: contain;
  display: block;
`;

const Meta = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Name = styled.div`
  font-weight: 900;
  color: #111827;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tag = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.10);
  border: 1px solid rgba(15, 118, 110, 0.18);
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

const SkeletonGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
`;

const Skeleton = styled.div`
  height: 150px;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.1s infinite linear;

  @keyframes shimmer {
    0% { background-position: 0% 0; }
    100% { background-position: 200% 0; }
  }
`;

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const snap = await getDocs(collection(firestore, "sponsors"));
        const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // FALLANDE: största först
        list.sort((a, b) => (b.paymentAmount || 0) - (a.paymentAmount || 0));

        setSponsors(list);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  const tierOf = (amt) =>
    (amt || 0) >= 5000 ? "Guld" : (amt || 0) >= 2000 ? "Silver" : "Brons";

  const countText = useMemo(() => {
    if (loading) return "Laddar sponsorer…";
    return sponsors.length ? `${sponsors.length} sponsorer` : "Inga sponsorer hittades ännu.";
  }, [loading, sponsors.length]);

  return (
    <Page>
      <Container>
        <Hero>
          <Title>Sponsorer</Title>
          <Subtitle>
            {countText} – tack för att ni stöttar Hofors BGK och hjälper oss skapa en grym säsong!
          </Subtitle>

          <Actions>
            <Btn to="/">Tillbaka till startsidan</Btn>
            <BtnGhost href="mailto:info@hoforsbgk.se">Bli sponsor</BtnGhost>
          </Actions>
        </Hero>

        {loading ? (
          <SkeletonGrid>
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} />
            ))}
          </SkeletonGrid>
        ) : (
          <Grid>
            {sponsors.map((s) => {
              const link = s.sponsorLink?.trim();
              const name = s.name || s.companyName || "Sponsor";
              const tag = tierOf(s.paymentAmount);

              const content = (
                <CardInner>
                  <Logo src={s.imageUrl} alt={name} />
                  <Meta>
                    <Name title={name}>{name}</Name>
                    <Tag>{tag}</Tag>
                  </Meta>
                </CardInner>
              );

              return (
                <Card key={s.id}>
                  {link ? (
                    <CardLink href={link} target="_blank" rel="noopener noreferrer">
                      {content}
                    </CardLink>
                  ) : (
                    content
                  )}
                </Card>
              );
            })}
          </Grid>
        )}
      </Container>
    </Page>
  );
};

export default Sponsors;


